import { SelectOrganization } from "@/components/organization/select-organization";
import { ThemeProvider } from "@/components/theme-provider";
import { organizationListQueryOptions } from "@/lib/queries/organization";
import { getCachedSession, sessionQueryOptions } from "@/lib/queries/session";
import { needsRoleSelection } from "@/lib/utils/organization";
import { createFileRoute, isRedirect, redirect } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";

const searchSchema = z
  .object({
    account_deleted: z.coerce.boolean().optional().catch(undefined),
  })
  .passthrough();

export const Route = createFileRoute("/")({
  validateSearch: searchSchema,
  // Auth guard copied from (app)/route.tsx since this route is outside that layout
  beforeLoad: async ({ context, location }) => {
    let session = getCachedSession(context.queryClient);

    if (session === undefined) {
      session = await context.queryClient.fetchQuery(sessionQueryOptions());
    }

    // Both user and session must exist for valid auth state
    if (!session?.user || !session?.session) {
      throw redirect({
        to: "/login",
        search: { returnTo: location.href },
      });
    }

    // Redirect rules — keep in sync with handleLoginSuccess in login.tsx
    try {
      const organizations = await context.queryClient.fetchQuery(
        organizationListQueryOptions(),
      );
      if (organizations && organizations.length > 0) {
        // Bare orgs (no organizationType) → role selection
        const allBare = organizations.every((o) =>
          needsRoleSelection(o.metadata as string | null),
        );
        if (allBare) {
          throw redirect({
            to: "/o/$orgSlug/select-role",
            params: { orgSlug: organizations[0].slug },
          });
        }

        // Single completed org → go straight to it
        if (organizations.length === 1) {
          throw redirect({
            to: "/o/$orgSlug",
            params: { orgSlug: organizations[0].slug },
          });
        }
      }
      // 0 orgs or multiple orgs → fall through to org picker
    } catch (error) {
      if (isRedirect(error)) throw error;
    }

    return { user: session.user, session };
  },
  component: SelectOrganizationPage,
});

function SelectOrganizationPage() {
  const { account_deleted: accountDeleted } = Route.useSearch();

  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <div className="relative flex min-h-svh flex-col items-center justify-center p-6 bg-muted/30 overflow-hidden">
        <div className="relative z-10 w-full max-w-md space-y-4">
          <div className="text-center space-y-1 mb-6">
            <h1 className="text-2xl font-bold tracking-tight">
              Select an Account
            </h1>
            <p className="text-muted-foreground">
              Choose an account to continue.
            </p>
          </div>
          {/* Hide Create Option on root page as requested */}
          <Suspense
            fallback={
              <div className="h-50 w-full animate-pulse rounded-lg border bg-card" />
            }
          >
            <SelectOrganization
              hideCreateOption={false}
              alert={
                accountDeleted
                  ? {
                      title: "Account Deleted",
                      description:
                        "Your account has been successfully deleted. Select another account or create a new one.",
                    }
                  : undefined
              }
            />
          </Suspense>
        </div>
      </div>
    </ThemeProvider>
  );
}
